import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

interface Props {
  ownerId: string;
}

interface BookingRow {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  parking_spot_id: string;
  created_at: string;
}

const EarningsTab = ({ ownerId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [spots, setSpots] = useState<Array<{ id: string; address: string; price_per_hour: number }>>([]);
  const [range, setRange] = useState<7 | 30 | 90>(30);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: spotData } = await supabase
        .from("parking_spots")
        .select("id, address, price_per_hour")
        .eq("owner_id", ownerId);
      const ids = (spotData ?? []).map((s) => s.id);
      setSpots(spotData ?? []);
      if (ids.length) {
        const { data: bk } = await supabase
          .from("bookings")
          .select("id, start_time, end_time, status, parking_spot_id, created_at")
          .in("parking_spot_id", ids)
          .eq("status", "completed");
        setBookings((bk as BookingRow[]) ?? []);
      }
      setLoading(false);
    };
    load();
  }, [ownerId]);

  const priceMap = useMemo(
    () => new Map(spots.map((s) => [s.id, Number(s.price_per_hour)])),
    [spots]
  );

  const calcEarnings = (b: BookingRow) => {
    const hrs = Math.max(
      1,
      Math.round((new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 3.6e6)
    );
    return hrs * (priceMap.get(b.parking_spot_id) ?? 0);
  };

  const chartData = useMemo(() => {
    const buckets = new Map<string, number>();
    const now = Date.now();
    for (let i = range - 1; i >= 0; i--) {
      const d = new Date(now - i * 86400000);
      buckets.set(d.toISOString().slice(0, 10), 0);
    }
    bookings.forEach((b) => {
      const key = new Date(b.created_at).toISOString().slice(0, 10);
      if (buckets.has(key)) buckets.set(key, (buckets.get(key) ?? 0) + calcEarnings(b));
    });
    return Array.from(buckets.entries()).map(([date, revenue]) => ({ date: date.slice(5), revenue }));
  }, [bookings, range, priceMap]);

  const perSpot = useMemo(() => {
    const map = new Map<string, { address: string; total: number; count: number }>();
    spots.forEach((s) => map.set(s.id, { address: s.address, total: 0, count: 0 }));
    bookings.forEach((b) => {
      const e = map.get(b.parking_spot_id);
      if (e) {
        e.total += calcEarnings(b);
        e.count += 1;
      }
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [bookings, spots, priceMap]);

  const totalEarnings = perSpot.reduce((acc, s) => acc + s.total, 0);

  const exportCsv = () => {
    const rows = [
      ["Spot", "Bookings", "Earnings (INR)"],
      ...perSpot.map((s) => [s.address, String(s.count), s.total.toFixed(2)]),
      ["TOTAL", String(bookings.length), totalEarnings.toFixed(2)],
    ];
    const csv = rows.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `earnings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading)
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          {[7, 30, 90].map((r) => (
            <Button
              key={r}
              size="sm"
              variant={range === r ? "default" : "outline"}
              onClick={() => setRange(r as 7 | 30 | 90)}
            >
              Last {r}d
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv}>
          <Download className="h-4 w-4 mr-2" /> Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Total Earnings: ₹{totalEarnings.toFixed(2)}</CardTitle>
          <CardDescription>Completed bookings in selected range</CardDescription>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="revenue" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Per-spot Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {perSpot.length === 0 ? (
            <p className="text-sm text-muted-foreground">No spots yet.</p>
          ) : (
            <ul className="divide-y">
              {perSpot.map((s, i) => (
                <li key={i} className="py-3 flex justify-between gap-4">
                  <span className="truncate">{s.address}</span>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {s.count} bookings · ₹{s.total.toFixed(0)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EarningsTab;
